import { foundation } from "@teaming/slice-engine";
import { OneOnOneTeamRoleType, TeamType } from "@teaming/common-enums";
// eslint-disable-next-line no-unused-vars
import { isValidEmail, materializeOneOnOne, alertFactory } from "@teaming/common-ui";
import { pristineState, pristineNewOneOnOneState } from "./state";

const noop = () => {};

const langNamespace = "teaming-web.store.host-dialogs";

export default function hostDialogsStoreFactory() {
	const { store, i18n, ioTeam, telemetry, navigation, } = foundation();

	const getUserProfile = () => store.getters[ "host-userProfile/userProfile" ];

	const translate = ( key, args ) => i18n.global.t( `${ langNamespace }.${ key }`, args );

	const { error, } = alertFactory( langNamespace, i18n.global.t );

	return store.registerModule( "host-dialogs", {
		namespaced: true,
		state: pristineState(),
		getters: {
			isConfirmationDialogOpen: state => state.isConfirmationDialogOpen,
			options: state => state.options,

			newOneOnOneDialog: state => state.newOneOnOneDialog,
		},
		mutations: {
			//
			// AppConfirmationDialog
			//
			requestUserConfirmation( state, { header, description, actions, metadata, } ) {
				state.options = { header, description, actions, metadata, };
				state.isConfirmationDialogOpen = true;
			},
			requestSimpleConfirmation( state, args ) {
				const {
					header,
					description,
					confirmText,
					cancelText,
					onConfirm,
					onCancel,
				} = args;
				const _onConfirm = ( onConfirm || noop );
				const _onCancel = ( onCancel || noop );
				state.options = {
					header,
					description,
					actions: [
						{
							id: "confirm",
							type: "PRIMARY",
							label: confirmText,
							isDestructive: !!onConfirm,
							onButtonPress: _onConfirm,
						},
						cancelText && {
							id: "cancel",
							type: "QUATERNARY",
							label: cancelText,
							isDestructive: false,
							onButtonPress: _onCancel,
						},
					].filter( a => !!a ),
				};
				state.isConfirmationDialogOpen = true;
			},
			toggleConfirmationDialog( state, { isOpen, } ) {
				state.isConfirmationDialogOpen = isOpen;
				if ( !isOpen ) {
					state.options = {};
				}
			},

			//
			// NewOneOnOneDialog
			//
			resetNewOneOnOneDialog( state, { teammates = [], isVisible = false, } = {} ) {
				const teammatesByEmail = teammates.reduce( ( acc, teammate ) => {
					acc[ teammate.email.toLowerCase() ] = teammate;
					return acc;
				}, {} );

				state.newOneOnOneDialog = {
					...pristineNewOneOnOneState(),
					teammatesByEmail,
					isVisible,
				};

				if ( isVisible ) {
					telemetry.oneonone.open1on1CreationDialog();
				}
			},
			updateNewOneOnOneDialog( state, form ) {
				const {
					canValidate,
					deferInvitation,
					teammateEmail,
					teammateRole,
				} = form;

				const currentUser = getUserProfile();

				const errors = {};
				if ( canValidate ) {
					if ( !isValidEmail( teammateEmail ) ) {
						errors.teammateEmail = translate( "emailFieldValidationError1" );
					} else if ( teammateEmail.toLowerCase() === currentUser.email.toLowerCase() ) {
						errors.teammateEmail = translate( "emailFieldValidationError2" );
					}
					if ( !OneOnOneTeamRoleType.includes( teammateRole ) ) {
						errors.teammateRole = translate( "roleFieldValidationError" );
					}
				}

				const lowerCaseEmail = teammateEmail.toLowerCase();
				const { teammatesByEmail, } = state.newOneOnOneDialog;
				const teammateMatches = Object.keys( teammatesByEmail )
					.filter( lowerCaseTeammateEmail => {
						return lowerCaseTeammateEmail !== lowerCaseEmail &&
							lowerCaseTeammateEmail.startsWith( lowerCaseEmail );
					} )
					.map( lowerCaseTeammateEmail => teammatesByEmail[ lowerCaseTeammateEmail ] );

				state.newOneOnOneDialog = {
					...state.newOneOnOneDialog,
					canValidate,
					deferInvitation,
					errors,
					isValid: !Object.keys( errors ).length,
					teammateEmail,
					teammateMatches,
					teammateRole,
				};
			},
		},
		actions: {
			async openNewOneOnOneDialog( { commit, } ) {
				try {
					const { personId: currentUserId, } = getUserProfile();

					const { data: myTeams, } = await ioTeam.fetchAllMyTeams( { limit: Number.MAX_SAFE_INTEGER, } );

					const { tmap, omap, } = myTeams.reduce( ( acc, team ) => {
						const { members, type, } = team;
						const map = type === TeamType.TEAM ? acc.tmap : acc.omap;
						members.forEach( member => {
							map[ member.personId ] = member;
						} );
						return acc;
					}, {
						tmap: {},
						omap: {},
					} );

					const potentials = Object.keys( tmap )
						.filter( memberId => memberId !== currentUserId && !omap[ memberId ] )
						.map( memberId => {
							return tmap[ memberId ];
						} );

					commit( "resetNewOneOnOneDialog", { teammates: potentials, isVisible: true, } );
				} catch ( e ) {
					error( "openNewOneOnOneDialogError", e );
				}
			},
			async submitNewOneOnOneDialog( { state, commit, } ) {
				const { newOneOnOneDialog: form, } = state;

				if ( !form.isValid ) {
					return;
				}

				let oneOnOneId;

				try {
					const { teammateEmail: email, teammateRole: oneOnOneRole, deferInvitation, } = form;

					const initiator = getUserProfile();
					const invitee = {
						name: "",
						email,
						oneOnOneRole,
						deferInvitation,
					};

					const oneOnOne = materializeOneOnOne( {
						initiator,
						invitee,
					} );

					const { data: createdOneOnOne, } = await ioTeam.createAndFavoriteOneOnOne( { oneOnOne, } );
					oneOnOneId = createdOneOnOne.id;

					commit( "resetNewOneOnOneDialog" );
				} catch ( e ) {
					error( "submitNewOneOnOneDialogError", e );
				}

				try {
					telemetry.oneonone.create1on1Relationship( {
						teamName: form.teammateEmail,
					} );
				} catch ( e ) {
					// eslint-disable-next-line no-console
					console.warn( "failed to send telemetry create1on1Relationship", e );
				}

				if ( oneOnOneId ) {
					setImmediate( () => {
						navigation.navigateToOneOnOneSharedStream( { teamId: oneOnOneId, } );
					} );
				}
			},
		},
	} );
}
